import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
// BACKEND_URL is resolved in lib/serverWake.js (single source of truth) — it
// rewrites localhost/127.0.0.1 to window.location.hostname so Chrome keeps
// requests same-site, preserving SameSite=Lax session cookies.
import { BACKEND_URL } from '../lib/serverWake';

const AuthContext = createContext(null);

/**
 * Read the CSRF token from the pp_csrf cookie (set by backend, non-httpOnly).
 * Required after page refresh: the httpOnly session cookie authenticates the user,
 * but the CSRF token (stored only in React state) is lost. The pp_csrf cookie
 * persists across refreshes so we can recover it here.
 */
function getCsrfFromCookie() {
  const match = document.cookie.match(/(?:^|;\s*)pp_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * AuthProvider - Provides authentication state and methods to the component tree.
 *
 * Uses httpOnly cookie-based authentication (Phase 21). No localStorage is involved.
 * The backend sets a `pp_session` httpOnly cookie during login, and a `pp_csrf`
 * non-httpOnly cookie for CSRF protection. This provider probes GET /api/auth/me
 * on mount to check for an existing session.
 *
 * State:
 *   - user (object|null): The current user profile
 *   - csrfToken (string|null): CSRF token recovered from pp_csrf cookie
 *   - isAuthenticated (boolean): Whether the user is logged in (derived from user)
 *   - isLoading (boolean): True during the initial session probe on mount
 *
 * Methods:
 *   - login(user, csrfToken): Sets user and CSRF state after successful authentication
 *   - logout(): Calls POST /api/auth/logout, clears state
 *   - logoutEverywhere(): Calls POST /api/auth/logout-all (ends every session), clears state
 *   - updateUser(user): Updates user in state (e.g., after API key regeneration)
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Latest user, readable from event listeners without re-subscribing them.
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // Tell the Chrome Extension (via its content script) that a user is logged in.
  // No-op if the extension is not installed. Called from login(), the session-restore
  // probe, and the content-script load handshake so the extension stays in sync even
  // when the user was already logged in before the content script was injected.
  const notifyExtensionLogin = useCallback((userData) => {
    if (!userData) return;
    try {
      window.postMessage({
        type: 'interceptapi-auth-event',
        action: 'login',
        payload: {
          apiKey: userData.apiKey,
          email: userData.email,
          displayName: userData.displayName
        }
      }, window.location.origin);
    } catch {
      // postMessage failure is non-critical
    }
  }, []);

  // One-time migration: remove stale localStorage entries from pre-cookie auth era.
  useEffect(() => {
    localStorage.removeItem('pp_token');
    localStorage.removeItem('pp_user');
  }, []);

  // Listen for session refresh events from the Chrome Extension content script.
  // When the extension sets/clears session cookies via chrome.cookies.set(),
  // it notifies open tabs via the content script, which dispatches this postMessage.
  // We re-probe GET /api/auth/me to pick up the updated session state.
  useEffect(() => {
    function handleExtensionSessionRefresh(event) {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'interceptapi-session-refresh') return;

      // Re-probe the session — the extension just set or cleared cookies. Only a definite
      // "no session" answer logs the website out: a network error or a 5xx during a
      // backend deploy used to clear the session in every open tab (audit F4).
      fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' })
        .then((res) => {
          if (res.status === 401 || res.status === 403) {
            setUser(null);
            setCsrfToken(null);
            return null;
          }
          return res.ok ? res.json() : null;
        })
        .then((data) => {
          if (data && data.user) {
            setUser(data.user);
            // Prefer the token from the response body — on split-domain deploys the
            // pp_csrf cookie lives on the backend domain and isn't readable via
            // document.cookie, so cookie recovery returns null after a reload.
            const csrf = data.csrfToken || getCsrfFromCookie();
            if (csrf) setCsrfToken(csrf);
          }
        })
        .catch(() => { /* network error — keep the current state */ });
    }

    window.addEventListener('message', handleExtensionSessionRefresh);
    return () => window.removeEventListener('message', handleExtensionSessionRefresh);
  }, []);

  // The content script asks for the current auth state when it loads (it may be
  // injected AFTER login() already fired, e.g. for an already-logged-in user).
  // Re-broadcast the current session so the extension picks it up, closing the
  // "logged in before the content script existed" race.
  useEffect(() => {
    function handleAuthRequest(event) {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'interceptapi-auth-request') return;
      if (userRef.current) notifyExtensionLogin(userRef.current);
    }
    window.addEventListener('message', handleAuthRequest);
    return () => window.removeEventListener('message', handleAuthRequest);
  }, [notifyExtensionLogin]);

  // Probe for existing httpOnly session cookie on mount.
  // If the user has a valid pp_session cookie (set during login, 7-day expiry),
  // the browser sends it automatically with credentials:'include'.
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    // A network error (backend still waking, a blip) is retried once before the login
    // page is shown to a user whose cookie is perfectly valid (audit F4). A 401/403 is a
    // real "no session" and ends the probe at once.
    const probe = (attempt) => {
      fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      })
        .then((res) => {
          if (!res.ok) throw Object.assign(new Error('Not authenticated'), { status: res.status });
          return res.json();
        })
        .then((data) => {
          if (!cancelled && data.user) {
            setUser(data.user);
            // Prefer the token from the response body — on split-domain deploys the
            // pp_csrf cookie lives on the backend domain and isn't readable via
            // document.cookie, so cookie recovery returns null after a reload.
            const csrf = data.csrfToken || getCsrfFromCookie();
            if (csrf) setCsrfToken(csrf);
            // Already-logged-in user opened the site — sync the extension too.
            notifyExtensionLogin(data.user);
          }
          if (!cancelled) setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          const definite = err && (err.status === 401 || err.status === 403);
          if (!definite && attempt < 1) {
            retryTimer = setTimeout(() => probe(attempt + 1), 3000);
            return;
          }
          // No valid session cookie — user must log in
          setIsLoading(false);
        });
    };
    probe(0);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [notifyExtensionLogin]);

  const login = useCallback((userData, csrf) => {
    setUser(userData);
    setCsrfToken(csrf);
    // Notify the extension (no-op if not installed).
    notifyExtensionLogin(userData);
  }, [notifyExtensionLogin]);

  const logout = useCallback(async (csrfTokenOverride) => {
    // Call server to clear the httpOnly session cookie
    const tokenToUse = csrfTokenOverride || csrfToken;
    let res = null;
    try {
      const headers = {};
      if (tokenToUse) headers['X-CSRF-Token'] = tokenToUse;

      res = await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
    } catch {
      // Server unreachable — still clear client state.
      // The cookie will expire on its own.
    }
    // The server answered but refused (CSRF mismatch, 5xx): the httpOnly cookie is still
    // valid, so clearing the page state here only LOOKED like a logout — the next visitor
    // of this browser was still signed in (audit F2). Keep the session and report it.
    if (res && !res.ok && res.status !== 401) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Could not log out (HTTP ${res.status}) — please try again`);
    }

    // Clean up any stale localStorage from the old auth system
    localStorage.removeItem('pp_token');
    localStorage.removeItem('pp_user');

    // Clear all client-side auth state
    setUser(null);
    setCsrfToken(null);

    // Notify the Chrome Extension content script (if present) about the logout.
    try {
      window.postMessage({
        type: 'interceptapi-auth-event',
        action: 'logout'
      }, window.location.origin);
    } catch {
      // postMessage failure is non-critical
    }
  }, [csrfToken]);

  // End the session on EVERY device (B-25): the server bumps the account's session
  // version so all earlier tokens are refused, then the local state is cleared like a
  // normal logout. The extension follows once it sees the website session is gone.
  const logoutEverywhere = useCallback(async () => {
    const headers = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    const res = await fetch(`${BACKEND_URL}/api/auth/logout-all`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Could not log out everywhere (HTTP ${res.status})`);
    }
    setUser(null);
    setCsrfToken(null);
    try {
      window.postMessage({ type: 'interceptapi-auth-event', action: 'logout' }, window.location.origin);
    } catch {
      // postMessage failure is non-critical
    }
  }, [csrfToken]);

  // The website session is gone (expired / ended elsewhere): forget it on this page only.
  // Unlike logout(), this neither calls the server nor tells the extension to sign out —
  // the extension's own key may still be perfectly valid (B-62).
  const clearWebsiteSession = useCallback(() => {
    setUser(null);
    setCsrfToken(null);
  }, []);

  // Merge, don't replace: regenerate-key used to return a 4-field object that wiped
  // role / createdAt / googleId from the profile (Admin menu and 'Member Since'
  // disappeared). The extension is told about the new key so it reconnects with it
  // instead of being cut off with the old one (B-39, B-24).
  const updateUser = useCallback((updatedUser) => {
    setUser((prev) => {
      const merged = { ...(prev || {}), ...(updatedUser || {}) };
      if (updatedUser && updatedUser.apiKey && (!prev || prev.apiKey !== updatedUser.apiKey)) {
        notifyExtensionLogin(merged);
      }
      return merged;
    });
  }, [notifyExtensionLogin]);

  const value = {
    user,
    csrfToken,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    logoutEverywhere,
    clearWebsiteSession,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth - Hook to consume auth context.
 *
 * Returns: { user, csrfToken, isAuthenticated, isLoading, login, logout, updateUser }
 *
 * Usage:
 *   const { user, isAuthenticated, csrfToken, login, logout } = useAuth();
 *
 * Throws if used outside of AuthProvider.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
